import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  ModalProps,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { NotificationTemplateTransport } from '../../../../enums';
import { type InfoFieldChangeHandler } from '../../../../components';
import { useTestNotificationMutation } from '../../../../api';
import type { DetailedNotificationTemplate } from '../../../../types';

type CreateFeeModalProps = {
  open: boolean;
  onClose: () => void;
  template: DetailedNotificationTemplate | undefined;
};

export const TestModal: FC<CreateFeeModalProps> = (props) => {
  const { open, onClose, template } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // api
  const [testNotification, { isLoading: isTestNotificationLoading }] =
    useTestNotificationMutation();

  // fields control logic
  const [fields, setFields] = useState<Record<string, string> | null>(null);
  const fieldsHandler: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;

    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // set default values in fields
  useEffect(() => {
    if (!template) return;

    const specs = template.specification.reduce(
      (acc, spec) => ({ ...acc, [spec]: `R_${spec}` }),
      {}
    );

    setFields({
      transport: '',
      ...specs,
    });
  }, [template]);

  // validation for required fields
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    const requiredSpecs = template?.specification ?? [];
    const requiredFields = [...requiredSpecs, 'transport'];

    for (const field of requiredFields) {
      if (
        fields &&
        (fields[field] === undefined || fields[field] === '' || fields[field] === null)
      ) {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  // test handler
  const testTemplate = async () => {
    if (!template || !fields) return;

    console.log(fields);

    const specsArray = template.specification.map((spec) => fields[spec]);

    const args = {
      body: {
        transport: fields.transport as `${NotificationTemplateTransport}`,
        event: template.event_name,
        specs: specsArray,
      },
    };

    console.log(args);

    try {
      await testNotification(args).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // elements
  const transportsSelectOptions = Object.values(NotificationTemplateTransport).map((transport) => {
    return (
      <MenuItem key={transport} value={transport}>
        {transport}
      </MenuItem>
    );
  });

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('notification-template.test-modal.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            select
            name="transport"
            value={fields?.transport ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('notification-template.test-modal.fields.transport')}
            fullWidth
          >
            {transportsSelectOptions}
          </TextField>

          {template &&
            template.specification.map((spec) => {
              return (
                <TextField
                  key={spec}
                  name={spec}
                  value={fields?.[spec]}
                  onChange={(e) => fieldsHandler(e)}
                  // label={`*${spec}`}
                  label={spec}
                  fullWidth
                />
              );
            })}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => testTemplate()}
          disabled={isTestNotificationLoading || checkSubmitDisabling()}
        >
          {t('notification-template.test-modal.proceed')}
        </Button>

        <Button variant="outlined" onClick={() => onClose()} disabled={isTestNotificationLoading}>
          {t('notification-template.test-modal.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
